import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { BotTTSTextData, LLMFunctionCallStartedData, LLMFunctionCallStoppedData, PipecatClient, RTVIMessage, TranscriptData } from '@pipecat-ai/client-js'
import {
  PipecatClientProvider,
  PipecatClientAudio,
  PipecatClientVideo,
  usePipecatClient,
  usePipecatClientTransportState,
} from '@pipecat-ai/client-react'
import { SmallWebRTCTransport } from '@pipecat-ai/small-webrtc-transport'
import { config } from '../../config'
import { apiService } from '../../service/api'
import { trackEvent } from '../../utils/analytics'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import {
  MICROPHONE_FEATURE,
  asEmbedderBlock,
  classifyMicFailure,
  hasMediaDeviceSupport,
  waitForMicTrack,
  watchPermissionsPolicyViolations,
  type MicFailure,
} from '@/lib/micDiagnostics'
import { isCallHostSession, isInteractiveSession } from '@/lib/meetingSession'
import { openHostChannel, type CallHostEvent, type CallUiState } from '@/lib/callChannel'
import { CallSurface } from './CallSurface'
import { CallChat } from './CallChat'

const meetingApiBase = config.apiBaseUrl.replace(/\/+$/, '')

// Used until the per-meeting ICE config arrives, and whenever that fetch fails.
// STUN-only is what shipped before TURN: direct-path calls still connect, and
// only relay-dependent peers (carrier-grade NAT) are lost — strictly better
// than refusing to start the call.
const FALLBACK_ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]

/**
 * Fetch the ICE servers for this meeting.
 *
 * TURN credentials are short-lived and account-specific, so the backend mints
 * them per meeting rather than baking them into the bundle. Unauthenticated by
 * design — the meeting token is the credential, same as for the SSE event
 * stream — because this page also runs inside a headless Recall browser that
 * has no session.
 */
async function fetchIceServers(meetingToken: string): Promise<RTCIceServer[]> {
  try {
    const response = await fetch(`${meetingApiBase}/v1/meeting/${meetingToken}/ice-servers`)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const data = await response.json()
    const servers = data?.ice_servers
    if (!Array.isArray(servers) || servers.length === 0) {
      throw new Error('response contained no ice_servers')
    }
    return servers as RTCIceServer[]
  } catch (error) {
    console.warn('⚠️ [ICE_SERVERS] fetch failed, falling back to STUN-only:', error)
    return FALLBACK_ICE_SERVERS
  }
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error'

interface AgentPersona {
  image: string
  name: string
  role: string
}

interface ConversationStates {
  listening: boolean
  botListening: boolean
  thinking: boolean
  talking: boolean
  functionCalling: boolean
}

interface TranscriptState {
  userText: string
  botText: string
}

const getMeetingTokenFromURL = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('token')
}

interface CallbackRefs {
  setConversationStates:
    | ((updater: (prev: ConversationStates) => ConversationStates) => void)
    | null
  setTranscript: React.Dispatch<React.SetStateAction<TranscriptState>> | null
  setAgentPersona: React.Dispatch<React.SetStateAction<AgentPersona | null>> | null
  setMicFailure: React.Dispatch<React.SetStateAction<MicFailure | null>> | null
  userEnded: boolean
  /**
   * Set once a `permissions-policy-violation` report names `microphone`. Lives on
   * the ref bag (not state) because `onDeviceError` fires from the SDK's callback
   * closure and needs the current value without re-creating the client.
   */
  micPolicyBlocked: boolean
}

// Map transport states to our simple UI states
const getConnectionStatus = (
  transportState: string | undefined
): ConnectionStatus => {
  switch (transportState) {
    case 'connected':
    case 'ready':
      return 'connected'
    case 'connecting':
    case 'initializing':
    case 'initialized':
      return 'connecting'
    case 'error':
      return 'error'
    case 'disconnected':
    default:
      return 'disconnected'
  }
}

interface MeetingBotContentProps {
  meetingToken: string | null
  webrtcUrl: string
  callbackRefs: React.MutableRefObject<CallbackRefs>
  embedded: boolean
  /**
   * True when a person is at this browser. Gates every mic diagnostic: a headless
   * Recall/Attendee session must never gate its connection on a microphone it
   * doesn't have, nor paint a banner over the bot's face in a customer's meeting.
   */
  interactive: boolean
  /**
   * True when this window owns the mic + peer connection but the UI lives in the
   * board panel (see `@/lib/callChannel`). A host renders a minimal window because
   * nobody is meant to look at it.
   */
  callHost: boolean
}

const MeetingBotContent: React.FC<MeetingBotContentProps> = ({
  meetingToken,
  webrtcUrl,
  callbackRefs,
  embedded,
  interactive,
  callHost,
}) => {
  const pipecatClient = usePipecatClient()
  const transportState = usePipecatClientTransportState()
  const connectionStatus = getConnectionStatus(transportState)

  const [conversationStates, setConversationStates] =
    useState<ConversationStates>({
      listening: true,
      botListening: true,
      thinking: false,
      talking: false,
      functionCalling: false,
    })
  const [transcript, setTranscript] = useState<TranscriptState>({
    userText: '',
    botText: '',
  })
  const [agentPersona, setAgentPersona] = useState<AgentPersona | null>(null)
  const [botEnabled, setBotEnabled] = useState<boolean>(true)
  const { enabled: videoEnabled } = useApiFeatureFlag('heygen_video_enabled', false)
  const [micFailure, setMicFailure] = useState<MicFailure | null>(null)

  // ── Call-host relay ───────────────────────────────────────────────────────────
  // When this window is the host, it owns the mic and the peer connection but the
  // UI is the board panel's job. Mirror every state change onto the channel and
  // accept mute/end back. Deliberately unthrottled: this window is hidden for the
  // whole call, and Chrome clamps timers in hidden windows to >=1s (then ~1/min),
  // so any coalescing timer would turn smooth captions into bursts.
  const hostChannelRef = useRef<ReturnType<typeof openHostChannel> | null>(null)

  // Read via ref so the channel effect below doesn't need to re-open on every
  // keystroke of a transcript just to answer a late `hello`.
  const uiStateRef = useRef<CallUiState>({
    status: connectionStatus,
    states: conversationStates,
    userText: transcript.userText,
    botText: transcript.botText,
    persona: agentPersona,
    botEnabled,
    micFailure,
  })
  uiStateRef.current = {
    status: connectionStatus,
    states: conversationStates,
    userText: transcript.userText,
    botText: transcript.botText,
    persona: agentPersona,
    botEnabled,
    micFailure,
  }

  const publish = useCallback(
    (event: CallHostEvent) => {
      hostChannelRef.current?.post(event)
    },
    []
  )

  useEffect(() => {
    if (!callHost || !meetingToken) return

    const channel = openHostChannel(meetingToken, (command) => {
      switch (command.t) {
        case 'hello':
          // The panel just mounted (or remounted after a board reload) and has no
          // state. Without this reply it would render an empty call until the next
          // event happens to fire.
          channel.post({ t: 'snapshot', state: uiStateRef.current })
          break
        case 'mute':
          pipecatClient?.enableMic(command.enabled)
          break
        case 'end':
          window.close()
          break
      }
    })
    hostChannelRef.current = channel

    // Announce ourselves too — whichever side comes up second must still sync.
    channel.post({ t: 'snapshot', state: uiStateRef.current })

    const sayBye = () => channel.post({ t: 'bye' })
    window.addEventListener('pagehide', sayBye)

    return () => {
      window.removeEventListener('pagehide', sayBye)
      channel.post({ t: 'bye' })
      channel.close()
      hostChannelRef.current = null
    }
  }, [callHost, meetingToken, pipecatClient])

  // Mirror each slice of state as it changes.
  useEffect(() => {
    publish({ t: 'status', status: connectionStatus })
  }, [connectionStatus, publish])
  useEffect(() => {
    publish({ t: 'states', patch: conversationStates })
  }, [conversationStates, publish])
  useEffect(() => {
    publish({ t: 'transcript', userText: transcript.userText, botText: transcript.botText })
  }, [transcript, publish])
  useEffect(() => {
    if (agentPersona) publish({ t: 'persona', persona: agentPersona })
  }, [agentPersona, publish])
  useEffect(() => {
    publish({ t: 'botEnabled', botEnabled })
  }, [botEnabled, publish])
  useEffect(() => {
    publish({ t: 'mic', micFailure })
  }, [micFailure, publish])
  const eventSourceRef = useRef<EventSource | null>(null)
  const connectionStartTimeRef = useRef<number | null>(null)
  const prevConnectionStatusRef = useRef<ConnectionStatus>('disconnected')
  const botStoppedSpeakingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const userStoppedSpeakingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Track AI call connection/disconnection status changes
  useEffect(() => {
    const prev = prevConnectionStatusRef.current
    if (prev !== connectionStatus) {
      if (connectionStatus === 'connected' && prev !== 'connected') {
        connectionStartTimeRef.current = Date.now()
        trackEvent('ai_call-connected', {
          agent_name: agentPersona?.name,
          meeting_token_present: !!meetingToken,
        })
      } else if (connectionStatus !== 'connected' && prev === 'connected') {
        const duration = connectionStartTimeRef.current
          ? Date.now() - connectionStartTimeRef.current
          : 0
        trackEvent('ai_call-disconnected', {
          agent_name: agentPersona?.name,
          duration_ms: duration,
        })
        connectionStartTimeRef.current = null
      }
      prevConnectionStatusRef.current = connectionStatus
    }
  }, [connectionStatus, agentPersona, meetingToken])

  useEffect(() => {
    console.log('💬 Conversation state changed to:', conversationStates)
  }, [conversationStates])

  useEffect(() => {
    console.log('🤖 Bot enabled status changed to:', botEnabled)
  }, [botEnabled])

  // Watch for a Permissions-Policy block on `microphone`. This is what separates
  // "the embedder never allowed us to ask" from "the user said no" — both of which
  // otherwise surface only as `NotAllowedError: Permission denied`. Attached before
  // the connect effect below so the (buffered) report is recorded first.
  useEffect(() => {
    return watchPermissionsPolicyViolations([MICROPHONE_FEATURE], (feature) => {
      callbackRefs.current.micPolicyBlocked = true
      console.error(
        `❌ [MEETING_MIC_POLICY_BLOCKED] feature=${feature} origin=${window.location.origin} — this document was not granted the feature by its embedder`
      )
      // The report lands on a task while `getUserMedia` rejects on a microtask, so
      // a failure may already be on screen classified as `denied` — telling the
      // user to fix it in site settings, which cannot work. Correct it now that
      // the real cause is known.
      setMicFailure((prev) => (prev ? asEmbedderBlock(prev) : prev))
    })
  }, [callbackRefs])

  // SSE connection for real-time bot status updates
  useEffect(() => {
    if (!meetingToken) {
      return
    }

    let isCleanedUp = false
    let reconnectTimeout: NodeJS.Timeout | null = null

    const setupEventSource = () => {
      if (isCleanedUp) return

      const eventSource = new EventSource(
        `${meetingApiBase}/v1/meeting/${meetingToken}/events`
      )
      eventSourceRef.current = eventSource

      eventSource.onopen = () => {
        console.log('🔌 SSE connection opened for bot status events')
      }

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('📨 Received bot status event:', data)

          if (data.type === 'status_change') {
            setBotEnabled(data.enabled)
            console.log('🤖 Bot enabled status updated to:', data.enabled)
          }
        } catch (error) {
          console.error('Error parsing SSE event data:', error)
        }
      }

      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error)
        eventSource.close()

        if (!isCleanedUp) {
          reconnectTimeout = setTimeout(() => {
            console.log('🔄 Attempting SSE reconnection...')
            setupEventSource()
          }, 3000)
        }
      }
    }

    setupEventSource()

    return () => {
      isCleanedUp = true
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [meetingToken])

  // Register the callbacks with the client's callback refs and handle auto-connection
  useEffect(() => {
    if (!pipecatClient) {
      console.log('PipecatClient not available yet, waiting...')
      return
    }

    callbackRefs.current.setConversationStates = setConversationStates
    callbackRefs.current.setTranscript = setTranscript
    callbackRefs.current.setAgentPersona = setAgentPersona
    callbackRefs.current.setMicFailure = setMicFailure
    console.log('🔧 Registered scoped callbacks')

    const reportMicFailure = (error: unknown, context: string) => {
      const failure = classifyMicFailure(error, {
        policyBlocked: callbackRefs.current.micPolicyBlocked,
      })
      console.error(
        `❌ [MEETING_MIC_FAILED] context=${context} reason=${failure.reason} detail=${failure.detail} interactive=${interactive}`
      )
      // Headless (Recall/Attendee) sessions log only — see `interactive`.
      if (interactive) {
        setMicFailure(failure)
        trackEvent('ai_call-mic_failed', { reason: failure.reason, context })
      }
      return failure
    }

    const autoConnect = async () => {
      try {
        if (!meetingToken) {
          console.error(
            'Meeting token not found. Please use a valid meeting link or pass a token prop.'
          )
          return
        }

        if (!webrtcUrl) {
          console.error('Unable to create connection URL')
          return
        }

        console.log('🚀 Starting voice bot connection...')
        console.log('Current transport state:', pipecatClient.state)

        if (
          pipecatClient.state === 'connected' ||
          pipecatClient.state === 'connecting' ||
          pipecatClient.state === 'ready'
        ) {
          console.log(
            'Already connected/connecting/ready, skipping. Current state:',
            pipecatClient.state
          )
          return
        }

        // Resolve TURN before connecting. `connect()` passes `iceConfig`
        // straight through to SmallWebRTCTransport._connect, which assigns it
        // over the constructor's servers — so this is what the peer connection
        // actually gathers with.
        const connectParams = { iceConfig: { iceServers: await fetchIceServers(meetingToken) } }

        // The mic preflight is for a human at this browser. A headless
        // Recall/Attendee session renders this page as the bot's camera feed in a
        // customer's meeting: it has no microphone, and blocking the connection
        // there would silence a bot whose entire job is to speak. Skip straight to
        // connect — exactly the pre-existing behaviour.
        if (!interactive) {
          console.log('🎧 Headless session (no interactive flag) — skipping mic preflight')
          await pipecatClient.connect(connectParams)
          console.log('✅ Connection initiated successfully!')
          return
        }

        if (!hasMediaDeviceSupport()) {
          // No `navigator.mediaDevices` at all — an insecure context. Connecting
          // would produce a permanently deaf session.
          reportMicFailure(new TypeError('navigator.mediaDevices is unavailable'), 'preflight')
          return
        }

        // Acquire the mic EXPLICITLY before connecting. `connect()` awaits
        // `initDevices()` outside its own try/catch, so when device setup rejects
        // the promise it returns never settles and the `catch` below is
        // unreachable. Calling it ourselves is the only way to observe that.
        try {
          await pipecatClient.initDevices()
        } catch (error) {
          reportMicFailure(error, 'initDevices')
          return
        }

        // Daily reports a blocked mic as a `camera-error` event rather than by
        // rejecting, so `initDevices()` can resolve having acquired nothing.
        // Assert the track positively — but WAIT for it first: Daily can populate
        // it just after `initDevices()` resolves, and an immediate check would call
        // a healthy call deaf, stick a permanent banner on it, and emit a bogus
        // `ai_call-mic_failed`. Skip the wait when the embedder has already been
        // caught blocking us — there is nothing to wait for.
        const policyBlocked = callbackRefs.current.micPolicyBlocked
        const gotTrack = policyBlocked
          ? false
          : await waitForMicTrack(() => !!pipecatClient.tracks().local.audio)

        if (!gotTrack) {
          reportMicFailure(
            {
              type: 'permissions',
              devices: ['mic'],
              message: 'initDevices resolved with no local audio track',
              // Without this the failure classifies as `denied` and tells the user
              // to change site settings, which does nothing when the embedder is
              // the one refusing.
              details: policyBlocked ? { blockedBy: 'browser' } : {},
            },
            'no-track'
          )
        } else {
          setMicFailure(null)
        }

        // Do NOT block the connection on the above. Even a genuinely deaf session
        // should connect so the user sees the banner in context rather than a dead
        // panel, and so a late track can still self-heal it (see
        // `onUserStartedSpeaking`).

        console.log('🎯 Connecting to:', webrtcUrl)

        await pipecatClient.connect(connectParams)

        console.log('✅ Connection initiated successfully!')
      } catch (error) {
        console.error('Connection initiation error:', error)
      }
    }

    console.log('Current pipecatClient state on mount:', pipecatClient.state)

    const timeoutId = setTimeout(autoConnect, 300)

    return () => {
      clearTimeout(timeoutId)
      if (botStoppedSpeakingTimeoutRef.current) {
        clearTimeout(botStoppedSpeakingTimeoutRef.current)
        botStoppedSpeakingTimeoutRef.current = null
      }
      if (userStoppedSpeakingTimeoutRef.current) {
        clearTimeout(userStoppedSpeakingTimeoutRef.current)
        userStoppedSpeakingTimeoutRef.current = null
      }
      callbackRefs.current.setConversationStates = null
      callbackRefs.current.setTranscript = null
      callbackRefs.current.setAgentPersona = null
      callbackRefs.current.setMicFailure = null
      if (pipecatClient && pipecatClient.state !== 'disconnected') {
        pipecatClient
          .disconnect()
          .catch((e) => console.warn('Cleanup disconnect error:', e))
      }
    }
    // `interactive` is derived from the token prop + URL, so it is stable for the
    // life of this component and cannot cause a reconnect loop.
  }, [pipecatClient, meetingToken, webrtcUrl, callbackRefs, interactive])

  const avatarSize = embedded ? 220 : 400

  // Host window: the board panel shows the avatar and captions, so this window is
  // the call's chat. It also keeps the way out: without the End button, someone who
  // navigates away from the board item is left with a window they can't cleanly kill.
  if (callHost) {
    return (
      <CallChat
        status={connectionStatus}
        persona={agentPersona}
        onEnd={() => window.close()}
      />
    )
  }

  const surface = (
    <CallSurface
      state={uiStateRef.current}
      compact={embedded}
      avatarSize={avatarSize}
      onToggleListening={
        interactive && meetingToken
          ? (listening) => {
              apiService.setMeetingListening(meetingToken, listening).catch((err: unknown) => {
                console.error('[MeetingBot] Listening command failed:', err)
              })
            }
          : undefined
      }
      videoSlot={
        videoEnabled ? (
          <PipecatClientVideo
            participant="bot"
            fit="cover"
            style={{
              width: `${avatarSize}px`,
              height: `${avatarSize}px`,
              borderRadius: `${avatarSize / 2}px`,
              overflow: 'hidden',
              backgroundColor: '#1f2937',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              position: 'relative',
              zIndex: 10,
            }}
          />
        ) : undefined
      }
    />
  )

  if (embedded) {
    return (
      <div className="w-full h-full bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center gap-2 px-2 py-2">{surface}</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="container mx-auto max-w-4xl px-4">
        <div className="flex flex-col items-center justify-center gap-4">{surface}</div>
      </div>
    </div>
  )
}

export interface MeetingBotProps {
  /**
   * Meeting token. When omitted, falls back to reading `?token=` from
   * the page URL — preserves the standalone `/meeting` route used by
   * Recall / Attendee embeds.
   */
  token?: string
  /**
   * When true, render in a compact PiP-friendly layout (smaller avatar,
   * tighter spacing, no full-screen background) so the component can
   * be embedded inside `<InAppMeetingPanel>`.
   */
  embedded?: boolean
}

const MeetingBot: React.FC<MeetingBotProps> = ({ token, embedded = false }) => {
  // Resolve the meeting token: prop wins, otherwise fall back to URL
  // (the standalone `/meeting?token=...` Recall/Attendee entry point).
  const meetingToken = useMemo(() => token ?? getMeetingTokenFromURL(), [token])

  // A `token` prop means a component mounted us for a person (the in-app panel).
  // Otherwise the token came from the URL, which is shared by the human-facing
  // popup (`?interactive=1`) and the headless Recall/Attendee camera-feed page
  // (bare `?token=`). Defaulting to non-interactive keeps the bot path untouched.
  const interactive = useMemo(() => isInteractiveSession(token), [token])

  // Host mode is URL-only: it is set exclusively by `callWindowUrl` on the popup an
  // embedded board opens. A prop-mounted panel is never a host.
  const callHost = useMemo(() => (token != null ? false : isCallHostSession()), [token])

  const webrtcUrl = useMemo(() => {
    return meetingToken
      ? `${meetingApiBase}/v1/meeting/offer?meeting_token=${meetingToken}`
      : ''
  }, [meetingToken])

  // Component-scoped callback ref bag — replaces the module-level
  // `setGlobal*` globals so multiple <MeetingBot/>s can coexist.
  const callbackRefs = useRef<CallbackRefs>({
    setConversationStates: null,
    setTranscript: null,
    setAgentPersona: null,
    setMicFailure: null,
    userEnded: false,
    micPolicyBlocked: false,
  })

  // Construct a fresh PipecatClient per meeting token. `useMemo` keyed
  // on `meetingToken` ensures we don't leak the previous session's
  // transport when the in-app panel restarts with a new token.
  const client = useMemo(() => {
    return new PipecatClient({
      transport: new SmallWebRTCTransport({
        // Superseded per connection by the `iceConfig` passed to connect();
        // this is only the floor if that fetch is skipped entirely.
        iceServers: FALLBACK_ICE_SERVERS,
        webrtcRequestParams: {
          endpoint: webrtcUrl,
        },
        waitForICEGathering: true,
      }),
      enableMic: true,
      enableCam: false,
      callbacks: {
        onError: (message) => {
          console.error('PipecatClient Error:', message)
        },
        // The SDK routes ALL device failures here — not to `onError`, and not by
        // rejecting `initDevices()`. Leaving it unregistered is how a blocked mic
        // became invisible: Daily names the cause precisely (including whether the
        // browser or the user blocked it) and the app was discarding it.
        onDeviceError: (error) => {
          const failure = classifyMicFailure(error, {
            policyBlocked: callbackRefs.current.micPolicyBlocked,
          })
          console.error(
            `❌ [MEETING_MIC_FAILED] context=onDeviceError reason=${failure.reason} detail=${failure.detail} interactive=${interactive}`
          )
          // Headless Recall/Attendee browsers routinely have no mic and fire this
          // on every session. Reporting it there would paint an error banner over
          // the bot's face in a customer's meeting and flood analytics.
          if (!interactive) return
          trackEvent('ai_call-mic_failed', { reason: failure.reason, context: 'onDeviceError' })
          callbackRefs.current.setMicFailure?.(failure)
        },
        onServerMessage: (message: RTVIMessage) => {
          console.log('Server Message:', message)

          if (message && typeof message === 'object' && 'type' in message && message.type === 'agent-persona') {
            const payload = 'payload' in message ? message.payload as { image: string; name: string; role: string } : null
            if (payload) {
              console.log('Agent Persona received:', payload)
              const rawImage = payload.image ?? ''
              const image = rawImage.startsWith('/')
                ? rawImage
                : rawImage
                  ? apiService.getUrl(rawImage)
                  : ''
              callbackRefs.current.setAgentPersona?.({
                image,
                name: payload.name,
                role: payload.role,
              })
            }
          }

          // The listen gate's state (/listenoff, /listenon, the automatic switch around a
          // lookup): what the Listening control shows, and the only word an in-app call gets.
          if (message && typeof message === 'object' && 'type' in message && message.type === 'bot-listening-state') {
            const payload = 'payload' in message ? message.payload as { listening?: boolean } : null
            if (payload && typeof payload.listening === 'boolean') {
              callbackRefs.current.setConversationStates?.((prev) => ({ ...prev, botListening: payload.listening === true }))
            }
          }

          if (message && typeof message === 'object' && 'type' in message && message.type === 'bot-awake-state') {
            const payload = 'payload' in message ? message.payload as { awake?: boolean } : null
            if (payload && typeof payload.awake === 'boolean') {
              console.log('Bot awake state received:', payload.awake)
              callbackRefs.current.setConversationStates?.((prev) => ({ ...prev, listening: payload.awake === true }))
            }
          }
        },
        onUserTranscript: (transcript: TranscriptData) => {
          console.log('User Transcript:', transcript)
          callbackRefs.current.setTranscript?.((prev) => ({
            ...prev,
            userText: transcript.text,
          }))
        },
        onBotTtsText: (transcript: BotTTSTextData) => {
          console.log('Bot TTS Text:', transcript)
          callbackRefs.current.setTranscript?.((prev) => ({
            ...prev,
            botText: prev.botText ? prev.botText + ' ' + transcript.text : transcript.text,
          }))
        },
        onConnected: () => {
          console.log('PipecatClient Connected!')
        },
        onDisconnected: () => {
          console.log('PipecatClient Disconnected!')
          callbackRefs.current.setConversationStates?.((prev) => ({
            ...prev,
            listening: false,
            thinking: false,
            talking: false,
            functionCalling: false,
          }))
          // Respect the user-ended flag set by <InAppMeetingPanel>'s
          // end-call handler so we don't auto-reconnect after a
          // deliberate teardown.
          if (webrtcUrl && !callbackRefs.current.userEnded) {
            setTimeout(async () => {
              if (callbackRefs.current.userEnded) {
                return
              }
              try {
                console.log('🔄 Attempting automatic reconnection...')
                await client.connect({})
                console.log('✅ Automatic reconnection successful!')

                // The reconnect goes through `connect()`, which runs device setup
                // itself — via the path that swallows failures. `onDeviceError`
                // covers whatever Daily reports, but a reconnect that comes back
                // with no track at all would otherwise be silently deaf again.
                if (
                  interactive &&
                  !(await waitForMicTrack(() => !!client.tracks().local.audio))
                ) {
                  const failure = classifyMicFailure(
                    {
                      type: 'permissions',
                      devices: ['mic'],
                      message: 'reconnected with no local audio track',
                    },
                    { policyBlocked: callbackRefs.current.micPolicyBlocked }
                  )
                  console.error(
                    `❌ [MEETING_MIC_FAILED] context=reconnect reason=${failure.reason} detail=${failure.detail}`
                  )
                  trackEvent('ai_call-mic_failed', {
                    reason: failure.reason,
                    context: 'reconnect',
                  })
                  callbackRefs.current.setMicFailure?.(failure)
                }
              } catch (error) {
                console.error('❌ Automatic reconnection failed:', error)
              }
            }, 1000)
          }
        },
        onUserStartedSpeaking: () => {
          console.log('User started speaking')
          // Voice activity on the local track is proof the mic works, whatever an
          // earlier assertion concluded. This is what clears a false-negative
          // `no-track` banner rather than leaving it up for the whole call.
          callbackRefs.current.setMicFailure?.(null)
          callbackRefs.current.setTranscript?.((prev) => ({
            ...prev,
            botText: '',
          }))
        },
        onUserStoppedSpeaking: () => {
          console.log('User stopped speaking')
          // Note: this timer is intentionally not refed here (callback is
          // constructed inside useMemo) — the underlying behavior matches
          // the pre-refactor module-level timer.
          setTimeout(() => {
            callbackRefs.current.setTranscript?.((prev) => ({ ...prev, userText: '' }))
          }, 1000)
        },
        onBotStartedSpeaking: () => {
          console.log('Bot started speaking')
          callbackRefs.current.setTranscript?.((prev) => ({
            ...prev,
            userText: '',
          }))
          callbackRefs.current.setConversationStates?.((prev) => ({
            ...prev,
            talking: true,
          }))
        },
        onBotStoppedSpeaking: () => {
          console.log('Bot stopped speaking')
          callbackRefs.current.setConversationStates?.((prev) => ({ ...prev, talking: false }))
          setTimeout(() => {
            callbackRefs.current.setTranscript?.((prev) => ({
              ...prev,
              botText: '',
            }))
          }, 1000)
        },
        onBotLlmStarted: () => {
          console.log('Bot LLM started thinking')
          callbackRefs.current.setConversationStates?.((prev) => ({
            ...prev,
            thinking: true,
          }))
        },
        onBotLlmStopped: () => {
          console.log('Bot LLM stopped thinking')
          callbackRefs.current.setConversationStates?.((prev) => ({ ...prev, thinking: false }))
        },
        onLLMFunctionCallStarted: (data: LLMFunctionCallStartedData) => {
          console.log('LLM function call started:', data.function_name)
          callbackRefs.current.setConversationStates?.((prev) => ({
            ...prev,
            functionCalling: true,
          }))
        },
        onLLMFunctionCallStopped: (data: LLMFunctionCallStoppedData) => {
          console.log('LLM function call stopped:', data.function_name)
          callbackRefs.current.setConversationStates?.((prev) => ({
            ...prev,
            functionCalling: false,
          }))
        },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingToken])

  // Reset the user-ended flag whenever we swap to a new token.
  useEffect(() => {
    callbackRefs.current.userEnded = false
    return () => {
      // Mark user-ended on unmount so the in-flight `onDisconnected`
      // callback skips auto-reconnect.
      callbackRefs.current.userEnded = true
    }
  }, [meetingToken])

  return (
    <PipecatClientProvider client={client}>
      <MeetingBotContent
        meetingToken={meetingToken}
        webrtcUrl={webrtcUrl}
        callbackRefs={callbackRefs}
        embedded={embedded}
        interactive={interactive}
        callHost={callHost}
      />
      <PipecatClientAudio />
    </PipecatClientProvider>
  )
}

export default MeetingBot
